/**
 * 性能监控工具函数
 * 提供页面加载性能监控的核心功能
 */

export interface PerformanceMetrics {
  // 基础时间戳
  trigger_ts: number;
  
  // 页面加载时间
  domContentLoaded: number;
  loadComplete: number;
  
  // 资源加载时间
  firstPaint: number;
  firstContentfulPaint: number;
  largestContentfulPaint: number;
  
  // 交互时间
  firstInputDelay: number;
  timeToInteractive: number;
  
  // 网络时间
  dnsLookup: number;
  tcpConnect: number;
  requestStart: number;
  responseStart: number;
  responseEnd: number;
}

/**
 * 获取页面性能指标
 */
export function getPerformanceMetrics(): PerformanceMetrics {
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  const paint = performance.getEntriesByType('paint');
  
  const metrics: PerformanceMetrics = {
    trigger_ts: Date.now(),
    domContentLoaded: 0,
    loadComplete: 0,
    firstPaint: 0,
    firstContentfulPaint: 0,
    largestContentfulPaint: 0,
    firstInputDelay: 0,
    timeToInteractive: 0,
    dnsLookup: 0,
    tcpConnect: 0,
    requestStart: 0,
    responseStart: 0,
    responseEnd: 0
  };

  if (navigation) {
    metrics.domContentLoaded = navigation.domContentLoadedEventEnd - navigation.fetchStart;
    metrics.loadComplete = navigation.loadEventEnd - navigation.fetchStart;
    metrics.dnsLookup = navigation.domainLookupEnd - navigation.domainLookupStart;
    metrics.tcpConnect = navigation.connectEnd - navigation.connectStart;
    metrics.requestStart = navigation.requestStart - navigation.fetchStart;
    metrics.responseStart = navigation.responseStart - navigation.fetchStart;
    metrics.responseEnd = navigation.responseEnd - navigation.fetchStart;
  }

  paint.forEach(entry => {
    if (entry.name === 'first-paint') {
      metrics.firstPaint = entry.startTime;
    }
    if (entry.name === 'first-contentful-paint') {
      metrics.firstContentfulPaint = entry.startTime;
    }
  });

  return metrics;
}

/**
 * 监听First Paint
 */
export function observeFP(callback: (fp: number) => void) {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.name === 'first-paint') {
          // 传递相对于页面导航开始的时间
          callback(entry.startTime);
        }
      });
    });
    
    observer.observe({ entryTypes: ['paint'] });
    
    return () => observer.disconnect();
  }
  return () => {};
}

/**
 * 监听First Contentful Paint
 */
export function observeFCP(callback: (fcp: number) => void) {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.name === 'first-contentful-paint') {
          // 传递相对于页面导航开始的时间
          callback(entry.startTime);
        }
      });
    });
    
    observer.observe({ entryTypes: ['paint'] });
    
    return () => observer.disconnect();
  }
  return () => {};
}

/**
 * 监听Largest Contentful Paint
 */
export function observeLCP(callback: (lcp: number) => void) {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      // 传递相对于页面导航开始的时间
      callback(lastEntry.startTime);
    });
    
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
    
    return () => observer.disconnect();
  }
  return () => {};
}

/**
 * 监听Cumulative Layout Shift
 */
export function observeCLS(callback: (cls: number) => void) {
  if ('PerformanceObserver' in window) {
    let clsValue = 0;
    let clsEntries: PerformanceEntry[] = [];
    
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        // 使用类型断言来处理PerformanceLayoutShift
        const layoutShiftEntry = entry as any;
        if (!layoutShiftEntry.hadRecentInput) {
          clsValue += layoutShiftEntry.value;
          clsEntries.push(entry);
        }
      });
      
      // 计算最终的CLS值
      callback(clsValue);
    });
    
    observer.observe({ entryTypes: ['layout-shift'] });
    
    return () => observer.disconnect();
  }
  return () => {};
}

/**
 * 监听First Input Delay
 */
export function observeFID(callback: (fid: number) => void) {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        const firstInputEntry = entry as PerformanceEventTiming;
        callback(firstInputEntry.processingStart - firstInputEntry.startTime);
      });
    });
    
    observer.observe({ entryTypes: ['first-input'] });
    
    return () => observer.disconnect();
  }
  return () => {};
}

/**
 * 统一监听所有Web Vitals指标
 */
export function observeWebVitals(callbacks: {
  onFP?: (fp: number) => void;
  onFCP?: (fcp: number) => void;
  onLCP?: (lcp: number) => void;
  onCLS?: (cls: number) => void;
  onFID?: (fid: number) => void;
}) {
  const cleanupFunctions: Array<() => void> = [];
  
  if (callbacks.onFP) {
    cleanupFunctions.push(observeFP(callbacks.onFP));
  }
  
  if (callbacks.onFCP) {
    cleanupFunctions.push(observeFCP(callbacks.onFCP));
  }
  
  if (callbacks.onLCP) {
    cleanupFunctions.push(observeLCP(callbacks.onLCP));
  }
  
  if (callbacks.onCLS) {
    cleanupFunctions.push(observeCLS(callbacks.onCLS));
  }
  
  if (callbacks.onFID) {
    cleanupFunctions.push(observeFID(callbacks.onFID));
  }
  
  return () => {
    cleanupFunctions.forEach(cleanup => cleanup());
  };
}

/**
 * 计算页面可交互时间
 */
export function calculateTimeToInteractive(): Promise<number> {
  return new Promise((resolve) => {
    if (document.readyState === 'complete') {
      resolve(performance.now());
      return;
    }

    const checkInteractive = () => {
      if (document.readyState === 'complete') {
        resolve(performance.now());
      } else {
        setTimeout(checkInteractive, 100);
      }
    };

    checkInteractive();
  });
}

/**
 * 获取资源加载性能
 */
export function getResourceTiming(): Array<{
  name: string;
  duration: number;
  size: number;
  type: string;
}> {
  const resources = performance.getEntriesByType('resource');
  
  return resources.map(resource => {
    const resourceEntry = resource as PerformanceResourceTiming;
    return {
      name: resourceEntry.name,
      duration: resourceEntry.duration,
      size: resourceEntry.transferSize || 0,
      type: resourceEntry.initiatorType
    };
  });
}

/**
 * 监控API请求性能
 */
export function createAPIPerformanceInterceptor() {
  const originalFetch = window.fetch;
  
  window.fetch = async function(...args) {
    const startTime = performance.now();
    const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
    
    try {
      const response = await originalFetch.apply(this, args);
      const endTime = performance.now();
      
      // 记录API请求性能（生产环境可移除console.log）
      // console.log(`API请求性能: ${url}`, {
      //   duration: endTime - startTime,
      //   status: response.status,
      //   timestamp: new Date().toISOString()
      // });
      
      return response;
    } catch (error) {
      const endTime = performance.now();
      
      // 生产环境可移除console.error
      // console.error(`API请求失败: ${url}`, {
      //   duration: endTime - startTime,
      //   error: error instanceof Error ? error.message : '未知错误',
      //   timestamp: new Date().toISOString()
      // });
      
      throw error;
    }
  };
}

/**
 * 获取页面加载状态
 */
export function getPageLoadState(): {
  readyState: string;
  isComplete: boolean;
  isInteractive: boolean;
} {
  return {
    readyState: document.readyState,
    isComplete: document.readyState === 'complete',
    isInteractive: document.readyState === 'interactive'
  };
}

/**
 * 等待页面完全加载
 */
export function waitForPageLoad(): Promise<void> {
  return new Promise((resolve) => {
    if (document.readyState === 'complete') {
      resolve();
      return;
    }

    window.addEventListener('load', () => {
      resolve();
    });
  });
}

/**
 * 等待DOM内容加载完成
 */
export function waitForDOMContentLoaded(): Promise<void> {
  return new Promise((resolve) => {
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      resolve();
      return;
    }

    document.addEventListener('DOMContentLoaded', () => {
      resolve();
    });
  });
}
